// Store time: a store's report reads on the store's own clock (stores.timezone), whatever
// the browser's zone, so a report shared between people in different places agrees.

// stores.timezone is free text with no check on write; a bad value reads as UTC, like the
// server's CSV export, instead of throwing.
export const safeZone = (timeZone: string): string => {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
};

// One reading of an instant on the store's clock, part by part (24-hour).
const zoneParts = (
  ms: number,
  timeZone: string,
  fields: Intl.DateTimeFormatOptions,
): ((type: Intl.DateTimeFormatPartTypes) => string) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    ...fields,
    timeZone: safeZone(timeZone),
    hourCycle: "h23",
  }).formatToParts(new Date(ms));
  return (type) => parts.find((p) => p.type === type)?.value ?? "";
};

export const formatInZone = (iso: string, timeZone: string): string => {
  const part = zoneParts(Date.parse(iso), timeZone, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${part("day")} ${part("month")} ${part("hour")}:${part("minute")}`;
};

// The store's wall-clock reading of an instant, as if that reading were UTC.
const wallClockMs = (ms: number, timeZone: string): number => {
  const part = zoneParts(ms, timeZone, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
  const n = (type: Intl.DateTimeFormatPartTypes): number => Number(part(type));
  return Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
};

// A <input type="datetime-local"> value ("2026-10-20T14:00") read on the store's clock.
// Two passes settle the zone offset, so dates on the far side of a DST change come out right.
export const zonedInputToIso = (value: string, timeZone: string): string => {
  const wall = Date.parse(`${value}Z`);
  const first = wall - (wallClockMs(wall, timeZone) - wall);
  const settled = wall - (wallClockMs(first, timeZone) - first);
  return new Date(settled).toISOString();
};

export const isoToZonedInput = (iso: string, timeZone: string): string =>
  new Date(wallClockMs(Date.parse(iso), timeZone)).toISOString().slice(0, 16);

export const formatClockInZone = (iso: string, timeZone: string): string => {
  const part = zoneParts(Date.parse(iso), timeZone, { hour: "2-digit", minute: "2-digit" });
  return `${part("hour")}:${part("minute")}`;
};
